import { z } from 'zod'

import { proposedGet, proposedPost } from '@/api/proposedEndpoint'
import {
  briefNarrativeSchema,
  briefVoteSchema,
  type BriefNarrative,
  type BriefVote,
} from '@/schemas/brief'

/**
 * Whether mm-be serves `/market/brief/` and `/market/brief/feedback/` (asks/brief.md,
 * 2026-10-28). While false nothing here is ever requested: the card and the reader write their
 * story from the market figures alone, "Coming up" and the feedback row show "Not available
 * yet". Flip it when mm-be ships, and the reader takes mm-be's story instead.
 */
export const BRIEF_API_AVAILABLE: boolean = false

const wireNarrativeSchema = z.object({
  as_of: z.string(),
  session: z.string(),
  headline: z.string(),
  summary: z.string(),
  lede: z.string(),
  read_minutes: z.number(),
  in_short: z.array(z.string()),
  sections: z.array(z.unknown()),
  terms: z.array(z.unknown()),
  my_vote: z.boolean().nullable().optional(),
})

export const briefService = {
  narrative: async (): Promise<BriefNarrative> => {
    const wire = wireNarrativeSchema.parse(await proposedGet<unknown>('/api/v5/market/brief/'))
    return briefNarrativeSchema.parse({
      asOf: wire.as_of,
      session: wire.session,
      headline: wire.headline,
      summary: wire.summary,
      lede: wire.lede,
      readMinutes: wire.read_minutes,
      inShort: wire.in_short,
      sections: wire.sections,
      terms: wire.terms,
      myVote: wire.my_vote ?? null,
    })
  },

  /** One vote per member per brief; a second answer replaces the first (an upsert). */
  vote: async (asOf: string, vote: BriefVote): Promise<BriefVote> =>
    briefVoteSchema.parse(
      await proposedPost<unknown>('/api/v5/market/brief/feedback/', {
        as_of: asOf,
        helpful: vote.helpful,
      }),
    ),
}
